// Copyright (c) Docugami, Inc. All rights reserved.

import { Icon, Stack } from "@fluentui/react";
import { DgBlueColoredButtonProps, DgColoredButton } from "components/common/DgCommonStyledComponents";
import { dgFontFamily } from "components/common/Fonts";
import { createFolderId } from "components/file_management/file_helpers";
import { useReportDocumentContext } from "components/reports/ReportDocumentContext";
import { backgroundGray, emptyColor, homeGray, primaryTheme, themeBlue } from "components/shell/presentation/AppStyles";
import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog";
import { IconRenderer } from "components/work_path/IconRenderer";
import { RequestState, useAsyncHelper } from "hooks/AsyncHelper";
import { useChunkSelection } from "hooks/chunkSelection";
import { dgIconNames } from "hooks/dgIcons";
import { Property, useDocumentProperties } from "hooks/documentProperties";
import { useAppSelector } from "hooks/redux";
import { ChunkInstance } from "networking/api";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDeleteAddedChunkDefinitionMutation } from "store/frontend/frontendSlice";
import styled, { css } from "styled-components";
import { SpanIdUtils } from "utils/SpanIdUtils";
import { isValidDecodedTag } from "utils/TagStandardUtils";
import { DocumentPropertiesViewer } from "./documentPropertiesViewer";
import { PropertyEditor } from "./propertyEditor";
import { StringPropertyKeys, useCollectionKeyFilter } from "hooks/collectionKeyFilter";
import { DgIconButton } from "components/shell/utils/DgIconButton";
import { SlidingFilterPanel } from "components/common/SlidingFilterPanel";
import { selectSelectedChunkInstance } from "store/sdvSelectors";

const EditorContainer = styled.div`
    box-sizing: border-box;
    padding: 0px 5px;
    flex: 1;
`

const ContentBorderCss = css`
    border: 1px solid ${emptyColor};
`

const ContentItemContainer = styled.div<{ border?: boolean, flex?: number }>`
    display: flex;
    flex: ${props => props.flex !== undefined ? props.flex : 1};
    ${props => !!props.border && ContentBorderCss}
    overflow: auto;
    overflow-x: hidden;
`

const ContentContainer = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    gap: 5px;
    height: 100%;
    overflow: hidden;
    padding: 10px 5px;
`

const NewPropertyButton = styled(DgColoredButton)`
    display: flex;
    align-items: center;
    align-self: center;
    justify-content: center;
    color: ${primaryTheme.palette.white};
    font-size: 10px;
    gap: 5px;
    box-sizing: border-box;
    padding: 4px 8px;
    border-radius: 4px;
`

const DisabledCss = css`
    cursor: wait;
    * {
        opacity: .8;
        pointer-events: none;
    }
`

const ContentRoot = styled.div<{ disabled?: boolean }>`
    display: flex;
    flex-direction: column;
    flex: 1;
    width: 100%;
    gap: 5px;
    background: ${primaryTheme.palette.white};
    ${props => props.disabled && DisabledCss};
    overflow: auto;
    padding: 10px 5px;
    box-sizing: border-box;
`

const Root = styled.div<{ hidden?: boolean }>`
    display: flex;
    flex-direction: column;
    width: 350px;
    max-width: 350px;
    min-height: ${props => !!props.hidden ? "0px" : "300px"};
    max-height:  ${props => !!props.hidden ? "0px" : "500px"};
    transition: height 0.3s ease-in-out;
    overflow: hidden;
    background: ${primaryTheme.palette.white};
    box-shadow: 0px 4px 4px 0px #00000040;
    margin: 5px; // for box shadow
`;

const Header = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 35px;
    align-items: center;
    justify-content: center;
    background-color: ${backgroundGray};

    position: relative;

    span {
        font-family: ${dgFontFamily};
        font-size: 14px;
        line-height: 35px;
        font-weight: 500;
        text-align: center;
    }
`;

const PlaceholderTextContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 30px;
    place-self: center;
    width: 80%;
    height: 100%;
    justify-content: center;
    align-items: center;
`;

const PlaceholderText = styled.span`
    place-self: center;
    font-family: ${dgFontFamily};
    font-size: 14px;
    font-weight: 500;
    line-height: 17px;
    letter-spacing: 0em;
    text-align: center;
`;

const searchPropertiesList: StringPropertyKeys<Property>[] = ['label'];

interface DocumentPropertiesPanelProps {
    documentId: string;
    setPanelHidden?: (v: boolean) => void;
    hidden?: boolean;
}

export const DocumentPropertiesPanel = (props: DocumentPropertiesPanelProps): JSX.Element => {
    const [selected, setSelected] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [pendingDelete, setPendingDelete] = useState<Property | null>(null);
    const [saveError, setSaveError] = useState(false);
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [filterText, setFilterText] = useState<string | undefined>(undefined);

    const selectedChunkInstance = useAppSelector(selectSelectedChunkInstance);
    const { selectChunk, clearSelection } = useChunkSelection();
    const isExternalModalActive = useAppSelector(state => state.documents.modalSelectionContext.isActive);
    const { properties, isLoading, isPropertyValid, addProperty, updateProperty } = useDocumentProperties(props.documentId);

    const [deleteDefinition] = useDeleteAddedChunkDefinitionMutation();

    const { canLinkChunk, isLinking, startLinkingChunk, cancel: cancelLinking } = useReportDocumentContext().chunkLinkingState;
    const isExternalProviderActive = useAppSelector(state => state.documents.modalSelectionContext.isActive && !isLinking);

    const canLink = (chunk: ChunkInstance) => !isExternalProviderActive && canLinkChunk(chunk);

    const lifetimeRef = useRef(true);
    useEffect(() => () => { lifetimeRef.current = false }, []);

    const { execute: executeDelete, state: deleteState } = useAsyncHelper("Delete property");
    const { execute: executeSave, state: saveState } = useAsyncHelper("Save property", selected);

    const selectedProperty = useMemo<Property | undefined>(() => {
        if (selected === '') {
            const property: Property = {
                spanId: '',
                definitionId: '',
                label: ''
            };

            return property;
        }
        else {
            return properties.find(x => x.spanId === selected);
        }
    }, [properties, selected]);

    const filteredProperties = useCollectionKeyFilter<Property>(filterText || '', properties, searchPropertiesList);

    useEffect(() => {
        if (saveState === RequestState.Error) {
            setSaveError(true);
        }
    }, [saveState]);

    useEffect(() => {
        // Reset editing/creating state when the panel is dismissed/hidden
        if (props.hidden) {
            if (selected === '') {
                setSelected(null);
            }
            setIsEditing(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.hidden])

    useEffect(() => {
        // Auto-dismiss the panel while navigating through documents
        props.setPanelHidden?.(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.documentId])

    useEffect(() => {
        if (!!selectedChunkInstance?.addedChunkInfo) {
            setSelected(selectedChunkInstance.spanId!);
            props.setPanelHidden?.(false);
        } else if (!isCreating) {
            setSelected(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedChunkInstance]);

    const deleteProperty = async (property: Property) => {
        const folderId = createFolderId(props.documentId);
        await deleteDefinition({ docsetId: folderId.clusterId, definitionId: property.definitionId });
        if (lifetimeRef.current) {
            if (property.spanId === selectedChunkInstance?.spanId) {
                select(null)
            }
        }
    }

    const saveProperty = async (property: Property) => {
        const folderId = createFolderId(props.documentId);
        if (!property.chunk) {
            // Create
            const response = await addProperty(property);

            if (lifetimeRef.current) {
                selectChunk(SpanIdUtils.createAddedChunk(folderId.fileId, response.definition.id).value);
            }
        }
        else {
            // Update
            await updateProperty(property.spanId, { value: property.value, label: property.label });

            if (lifetimeRef.current) {
                selectChunk(property.spanId);
            }
        }
    }

    function select(property?: Property | null) {
        if (!property) {
            setSelected(null);
            clearSelection();
        }
        else if (!!property.spanId) {
            selectChunk(property.spanId);
        }
    }

    function edit(property?: Property) {
        if (!property) {
            clearSelection();
            setSelected('');
        }
        else if (!!property.spanId) {
            selectChunk(property.spanId);
        }
        setIsEditing(true);
    }

    function cancelEdit() {
        if (isEditing) {
            clearSelection();
            setSelected(null);
            setIsEditing(false);
        }
    }

    function canDrag(p: Property): boolean {
        const hasValidTag = !!p.label && isValidDecodedTag(p.label);
        return !isExternalModalActive && !!p.chunk && (canLink(p.chunk) || hasValidTag);
    }

    function onDragStart(p: Property): void {
        if (canDrag(p)) {
            if (p.spanId !== selected) {
                select(p);
            }

            if (canLink(p.chunk!)) {
                startLinkingChunk(p.chunk!)
            }
        }
    }

    function onDragEnd(p: Property): void {
        if (isLinking) {
            cancelLinking();
        }
    }

    const isCreating = selected === '';
    const isEditorActive = isEditing && (!!selected || selected === '');

    const getCreateNewPropertyButton = () => (
        <NewPropertyButton disabled={isEditorActive} onClick={() => edit()} controlButtonProps={DgBlueColoredButtonProps}>
            <IconRenderer
                source={{
                    name: dgIconNames.DgPlusBox,
                    height: 1,
                    width: .877,
                    defaultColor: primaryTheme.palette.white
                }}
                width={14}
                height={14} />
            CREATE A PROPERTY
        </NewPropertyButton>
    )

    function getContent() {
        if (!isLoading && !properties.length && !isEditorActive) {
            return (
                <PlaceholderTextContainer>
                    <PlaceholderText>Properties allow you to add data to your documents. Click 'Create A Property’ to start.</PlaceholderText>
                    {getCreateNewPropertyButton()}
                </PlaceholderTextContainer>
            )
        }

        let content = (
            <ContentItemContainer
                flex={1.75}
                onClick={() => select(null)}>
                <DocumentPropertiesViewer
                    properties={filteredProperties}
                    selected={selectedProperty}
                    onSelect={p => select(p)}
                    onDelete={p => setPendingDelete(p)}
                    onEdit={p => edit(p)}
                    canDrag={canDrag}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    isLoading={isLoading} />
            </ContentItemContainer>
        );

        // Show the editor if we are editing a property, or creating a new one
        if (isEditorActive) {
            content = (
                <ContentItemContainer>
                    <EditorContainer>
                        <PropertyEditor
                            property={selectedProperty}
                            onCancel={cancelEdit}
                            onSave={p => executeSave(saveProperty(p))}
                            validate={isPropertyValid}
                            isSaving={saveState === RequestState.InProgress}
                            isNew={selected === ''} />
                    </EditorContainer>
                </ContentItemContainer>
            );
        }

        return (
            <>
                <Stack horizontal>
                    <Stack
                        horizontal
                        horizontalAlign="end"
                        styles={{
                            root: {
                                flex: 3,
                            }
                        }}>
                        {getCreateNewPropertyButton()}
                    </Stack>

                    <Stack
                        horizontal
                        horizontalAlign="end"
                        styles={{
                            root: {
                                flex: 1,
                            }
                        }}>
                        <DgIconButton
                            iconName={dgIconNames.DgFilter}
                            onClick={() => setShowFilterPanel(current => !current)}
                            height={16}
                            width={16}
                            color={showFilterPanel ? themeBlue : homeGray} />
                    </Stack>
                </Stack>
                <Stack>
                    <SlidingFilterPanel
                        showFilterPanel={showFilterPanel}
                        filterText={filterText}
                        onFilterTextChange={setFilterText}
                        onClose={() => setShowFilterPanel(false)}
                        countText={`Showing ${filteredProperties.length} of ${properties.length} properties`}
                    />
                </Stack>
                <ContentContainer>
                    {content}
                </ContentContainer>
            </>
        )
    }

    return (
        <Root hidden={props.hidden}>
            <Header>
                <span>PROPERTIES</span>
                <Icon iconName="ChromeClose"
                    onClick={() => props.setPanelHidden?.(true)}
                    styles={{ root: { position: 'absolute', right: '10px', fontSize: '10px', cursor: 'pointer' } }} />
            </Header>
            <ContentRoot disabled={saveState === RequestState.InProgress || deleteState === RequestState.InProgress}>
                {getContent()}
            </ContentRoot>
            <ConfirmationDialog
                hidden={!pendingDelete}
                title="Delete Property"
                subText="Are you sure you want to delete this property? This property will be removed for all documents in the doc set."
                confirmButtonText="Delete"
                dismissButtonText="Cancel"
                dismissedCallback={() => setPendingDelete(null)}
                confirmedCallback={() => {
                    executeDelete(deleteProperty(pendingDelete!));
                    setPendingDelete(null);
                }} />
            <ConfirmationDialog
                hidden={!saveError}
                title="Uh oh!"
                subText="Something went wrong when trying to save your property. Please try again later. If the problem persists, contact your Docugami representative."
                confirmButtonText="Ok"
                dismissedCallback={() => setSaveError(false)}
                confirmedCallback={() => setSaveError(false)} />
        </Root>
    );
}
